<template>
  <data-table
    :headers="headers"
    :items="rows"
    :options.sync="options"
    :no-data-text="$t('no_data')"
    :loading="loading"
  >
    <template #item.hidden="{ item }">
      <v-simple-checkbox
        :value="item.hidden"
        @input="$emit('toggleHidden', item)"
        data-testid="hidden_checkbox"
      />
    </template>
    <template #item.name="{ item }">
      <span data-testid="place_name">{{ item.name }}</span>
    </template>
    <template #item.last_upload_time="{ item }">
      {{ formatTimestamp(item.last_upload_time) }}
    </template>
    <template #item.recent_data="{ item }">
      <router-link
        class="text-body-1"
        :to="recentEventsUrl(item)"
      >
        {{ $t('button.show') }}
      </router-link>
    </template>
    <template #item.all_data="{ item }">
      <router-link
        class="text-body-1"
        :to="eventListUrl(item)"
        data-testid="place_events_link"
      >
        {{ $t('button.show') }}
      </router-link>
    </template>
    <template #item.floor_plans="{ item }">
      <router-link
        class="text-body-1"
        :to="floorPlansUrl(item)"
      >
        {{ $t('button.show') }}
      </router-link>
    </template>
    <template #item.assigneesText="{ item }">
      {{ item.assigneesText }}
    </template>
    <template #item.actions="{ item }">
      <v-btn
        icon
        @click="$emit('edit', item)"
        data-testid="edit_button"
      >
         <v-icon primary class="ma-2 material-icons">edit</v-icon>
      </v-btn>
      <v-tooltip
        left
        :disabled="item.deletable"
      >
        <template #activator="{ on, attrs }">
          <!-- Hover handlers don't work on disabled inputs (?) so attach to div around them -->
          <div
            class="d-inline-block"
            v-on="on"
            v-bind="attrs"
          >
            <v-btn
              icon
              @click="$emit('delete', item)"
              :disabled="!item.deletable"
              data-testid="delete_button"
            >
              <v-icon primary class="ma-2 material-icons">delete</v-icon>
            </v-btn>
          </div>
        </template>
        {{ $t('cannot_delete_explanation') }}
      </v-tooltip>
    </template>
  </data-table>
</template>

<i18n lang="yaml">
ja:
  column:
    hidden: 非表示
    name: 設置場所
    assignees: 担当者
    last_upload_time: 最終アップロード日時
    recent_data: 新着解析結果
    all_data: データ一覧
    floor_plans: 図面
  button:
    show: '表示する'
  no_data: データがありません
  cannot_delete_explanation: 設置場所にデータがあるので、削除できません

en:
  column:
    hidden: Hidden
    name: Installation site
    assignees: Assignees
    last_upload_time: Last upload time
    recent_data: New
    all_data: All Data
    floor_plans: Floor Plans
  button:
    show: 'Show'
  no_data: No data available
  cannot_delete_explanation: The site cannot be deleted since it contains data.
</i18n>

<script>
import Features from '@/mixins/features';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import DataTable from '@/components/atoms/DataTable';

export default {
  name: 'station-place-table',
  mixins: [
    Features,
    DateTimeFormatting,
  ],
  components: {
    DataTable,
  },
  props: {
    places: Array,
    loading: Boolean,
    users: { type: Array, default: () => [] },
    currentUserId: String,
    query: {
      type: Object,
      default: () => ({ searchText: '' }),
    },
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 100,
      },
    };
  },
  computed: {
    headers() {
      const baseHeaders = [];

      if (!this.featureIsActive('s_type_place_assignees')) {
        baseHeaders.push(
          { text: this.$t('column.hidden'), value: 'hidden', sortable: false, width: 60 },
          { text: this.$t('column.name'), value: 'name', cellClass: 'name-column' },
          { text: this.$t('column.last_upload_time'), value: 'last_upload_time', width: 180 },
          { text: this.$t('column.recent_data'), value: 'recent_data', sortable: false, width: 100 },
          { text: this.$t('column.all_data'), value: 'all_data', sortable: false, width: 100 },
        );

        baseHeaders.push({ text: this.$t('column.floor_plans'), value: 'floor_plans', sortable: false, width: 100 });

        baseHeaders.push({ text: '', value: 'actions', sortable: false, width: 110 });
        return baseHeaders;
      }

      baseHeaders.push(
        { text: this.$t('column.hidden'), value: 'hidden', sortable: false, width: 60 },
        { text: this.$t('column.name'), value: 'name', cellClass: 'name-column' },
        { text: this.$t('column.assignees'), value: 'assigneesText', sortable: true, width: 140 },
        { text: this.$t('column.last_upload_time'), value: 'last_upload_time', width: 180 },
        { text: this.$t('column.recent_data'), value: 'recent_data', sortable: false, width: 100 },
        { text: this.$t('column.all_data'), value: 'all_data', sortable: false, width: 100 },
      );

      baseHeaders.push({ text: this.$t('column.floor_plans'), value: 'floor_plans', sortable: false, width: 100 });

      baseHeaders.push({ text: '', value: 'actions', sortable: false, width: 110 });
      return baseHeaders;
    },
    placeViewModels() {
      if (!this.featureIsActive('s_type_place_assignees')) {
        return this.places;
      }

      return this.places.map(place => ({
        ...place,
        assigneesText: this.getAssigneeNames(place.assignee_ids),
      }));
    },
    rows() {
      return this.sortPlaces(
        this.filterPlaces(this.placeViewModels),
      );
    },
  },
  methods: {
    filterPlaces(places) {
      if (!this.query.searchText) {
        return places;
      }

      const searchLower = this.query.searchText.toLowerCase();
      return places.filter(place => {
        // Check if the search text matches the place name
        if (place.name.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Check if the search text matches any assignee name
        if (this.featureIsActive('s_type_place_assignees')) {
          if (place.assigneesText.toLowerCase().includes(searchLower)) {
            return true;
          }
        }

        return false;
      });
    },
    // vue-router automatically encodes URLs with spaces and redirects to the encoded URL
    // This causes the StationEventList to be loaded twice and cancel the event retrieval request
    // Because of encode the place already in advance to prevent the additional redirect
    eventListUrl(item) {
      return `/station/${encodeURI(item.id)}`;
    },
    recentEventsUrl(item) {
      return `/station/${encodeURI(item.id)}/recent`;
    },
    floorPlansUrl(item) {
      return `/station/${encodeURI(item.id)}/floor_plans`;
    },
    sortPlaces(places) {
      if (!this.featureIsActive('s_type_place_assignees')) {
        const placesWithUploadTime = places.filter(place => place.last_upload_time);
        const sortedPlacesWithUploadTime = this._.sortBy(
          placesWithUploadTime,
          'last_upload_time',
        ).reverse();

        const placesWithoutUploadTime = places.filter(place => !place.last_upload_time);
        const sortedPlacesWithoutUploadTime = this._.sortBy(placesWithoutUploadTime, 'name');

        return sortedPlacesWithUploadTime.concat(sortedPlacesWithoutUploadTime);
      }

      return [...places].sort((a, b) => {
        // 1. 担当者優先ソート
        if (this.currentUserId) {
          const aIsAssigned = a.assignee_ids?.includes(this.currentUserId);
          const bIsAssigned = b.assignee_ids?.includes(this.currentUserId);

          if (aIsAssigned && !bIsAssigned) return -1;
          if (!aIsAssigned && bIsAssigned) return 1;
        }

        // 2. 最終アップロード日時で降順ソート
        const aTime = a.last_upload_time ? new Date(a.last_upload_time).getTime() : 0;
        const bTime = b.last_upload_time ? new Date(b.last_upload_time).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;

        // 3. 最終的に名前順
        return a.name.localeCompare(b.name);
      });
    },
    getAssigneeNames(assigneeIds) {
      if (!assigneeIds || assigneeIds.length === 0) return '-';
      return assigneeIds
        .map(id => {
          const user = this.users.find(u => u.id === id);
          return user ? user.name : null;
        })
        .filter(name => name !== null)
        .sort()
        .join(', ') || '-';
    },
  },
};
</script>

<style scoped lang="sass">
::v-deep .name-column
  overflow-wrap: anywhere
</style>
